/** Which org a NEW session will belong to.
 *
 * This MIRRORS the backend's ``_resolve_org_id`` /
 * ``resolve_fallback_org_id`` precedence so the org we SHOW on the
 * create form is the org the session actually lands in. The create
 * request then sends this slug explicitly (``org_slug``), which takes
 * the backend's highest-priority branch — so display and reality are
 * the same value by construction rather than two rules that agreed
 * until they didn't.
 *
 * The bug this closes: with no pin, the backend falls back to the
 * user's SOLE non-root org, while the switcher could read as root (or
 * as nothing) — so a session the user believed was going to root was
 * created in their tenant instead, silently.
 */
export type TargetOrg = {
  slug: string;
  name: string;
  /** Why this org — drives the copy on the create form. */
  reason: "pinned" | "only-membership" | "root-default";
  /** The pinned org's name when the pin was passed over because the
   *  viewer (a platform admin) isn't a member of it. */
  skippedPin?: string;
};

export type OrgLike = {
  slug: string;
  name: string;
  is_root: boolean;
  membership_role: "member" | "manager" | "admin" | null;
};

export function resolveTargetOrg(
  pinnedSlug: string | null,
  orgs: readonly OrgLike[],
): TargetOrg | null {
  // 1. A pin of an org the viewer belongs to wins — including root. A
  //    platform admin may pin any org to administer it, but a customer
  //    org they aren't a member of never becomes the home of a new
  //    session by default: its credits, code sources and LLM keys would
  //    be used by accident. The backend applies the same rule.
  let skippedPin: string | undefined;
  if (pinnedSlug) {
    const pinned = orgs.find((o) => o.slug === pinnedSlug);
    if (pinned && pinned.membership_role != null) {
      return { slug: pinned.slug, name: pinned.name, reason: "pinned" };
    }
    if (pinned) skippedPin = pinned.name;
  }
  const withSkipped = (target: TargetOrg): TargetOrg =>
    skippedPin ? { ...target, skippedPin } : target;
  // 2. Exactly one non-root membership → that tenant (the backend's
  //    fallback: file the work, and bill the credits, where they live).
  const nonRootMemberships = orgs.filter(
    (o) => !o.is_root && o.membership_role != null,
  );
  if (nonRootMemberships.length === 1) {
    const only = nonRootMemberships[0]!;
    return withSkipped({ slug: only.slug, name: only.name, reason: "only-membership" });
  }
  // 3. Otherwise root, when it's visible to this user.
  const root = orgs.find((o) => o.is_root);
  if (root) {
    return withSkipped({ slug: root.slug, name: root.name, reason: "root-default" });
  }
  return null;
}
